import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';
import type { Params } from '@angular/router';
import isMobile from 'ismobilejs';
import { CookieService } from 'ngx-cookie-service';
import type { Observable } from 'rxjs';
import { catchError, concatMap, take } from 'rxjs/operators';

import { WidgetsService } from 'components/organisms/widgets-loader/widgets.service';
import { WIDGETS_CONFIG } from 'components/widgets/widgets-config';
import { EnvironmentService } from 'src/services/environment/environment.service';
import { HTTP_ERROR_HANDLER_SERVICE } from 'src/services/http-error-handler/entities/const';
import type { IHttpErrorHandlerService } from 'src/services/http-error-handler/entities/types';
import { InteractionStatSiteTrackingService } from 'src/services/tracking/implementations/interaction-stat-site-tracking/interaction-stat-site-tracking.service';
import { TypicalProjectsRestService } from 'src/services/typical-projects-rest/typical-projects-rest.service';

import { BaseResolver } from './base-resolver';
import type { IDefaultLayout, ILandingPage } from '../layouts/entities/types';

@Injectable()
export class GetMobileAppResolverService extends BaseResolver {
    protected readonly httpErrorHandlerService: IHttpErrorHandlerService;
    private readonly document = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    private cookieService = inject(CookieService);
    private interactionStatSiteTrackingService = inject(InteractionStatSiteTrackingService);
    private readonly restService = inject(TypicalProjectsRestService);
    private readonly widgetsService = inject(WidgetsService);
    readonly environmentService = inject(EnvironmentService);

    public queryParams: Params;

    private APP_STORE_URL = 'https://itunes.apple.com/us/app/wrike-project-management-collaboration/id890048871?mt=8';
    private GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.wrike';

    constructor() {
        const httpErrorHandlerService = inject<IHttpErrorHandlerService>(HTTP_ERROR_HANDLER_SERVICE);

        super(httpErrorHandlerService);

        this.httpErrorHandlerService = httpErrorHandlerService;
    }

    public resolve(): Observable<ILandingPage | IDefaultLayout> {
        if (isPlatformBrowser(this.platformId)) {
            this.getQueryParams();

            if (this.isTrackingAllowed()) {
                this.trackingRedirect(this.getGaValue());
            }

            this.redirectToStore();
        }

        return this.makePageRequest('get-mobile-app').pipe(
            take(1),
            concatMap((data) => this.handleSuccess(data)),
            concatMap((data) => this.widgetsService.resolveWidgetsFromPage(data, WIDGETS_CONFIG)),
            catchError((error) => this.handleError(error))
        );
    }

    private makePageRequest(rootSlug: string) {
        return this.restService.getProjectRootPage(rootSlug);
    }

    // Tech Debt: need to rewrite this function to Angular way https://www.wrike.com/open.htm?id=1233159660
    private getQueryParams(): void {
        const url = new URL(this.document.location.href);

        this.queryParams = {};

        url.searchParams.forEach((value, key) => {
            this.queryParams[key] = value;
        });
    }

    private getGaValue(): string | null {
        return this.queryParams['ga'] || this.cookieService.get('_ga') || null;
    }

    private isTrackingAllowed(): boolean {
        return Boolean(this.queryParams['at']);
    }

    private trackingRedirect(gaId: string) {
        this.interactionStatSiteTrackingService.track(
            {
                eventName: 'app_stores_qr_scanner__redirected',
                value: {
                    website_ga: gaId,
                },
            },
            this.environmentService.environment.apiOrigin
        );
    }

    private redirectToStore(): void {
        window.location.href = this.getStoreLinkByDevice(
            isMobile(window.navigator).android.device,
            isMobile(window.navigator).apple.device
        );
    }

    private getStoreLinkByDevice(isAndroid: boolean, isApple: boolean): string {
        if (isAndroid) {
            return this.GOOGLE_PLAY_URL;
        } else if (isApple) {
            return this.APP_STORE_URL;
        }
        return this.environmentService.environment.origin + '/try-mobile/';
    }
}
